import { CaretLeft } from "@phosphor-icons/react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import BasePhoneInput from "../../../components/BasePhoneInput";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import CurrencyField from "../../../components/CurrencyField";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import useUpdateEmploymentInfo from "../../../hooks/useUpdateEmploymentInfo";
import useVerifyBusiness from "../../../hooks/useVerifyBusiness"; // Import the hook
import { leaveSetupTask } from "../../../utils/setupTaskReturn";

const EmploymentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;
  const { refetch, user } = useAuth();
  const [isBusinessVerified, setIsBusinessVerified] = useState(false); // New state for verification status

  const { isUpdatingInfo, updateEmploymentInfo } = useUpdateEmploymentInfo({
    onSuccess(data: any) {
      if (data?.data?.reason) {
        toast.success("Something went wrong. Please try again.");
      } else {
        toast.success("Employment details updated successfully");
      }
      refetch();
      if (fromSetup) leaveSetupTask(navigate, location.state);
      else navigate("/home/settings/compliance");
    },
    onError() {
      toast.error("Failed to update employment details");
    },
  });

  const { verifyBusiness, isVerifyingBusiness } = useVerifyBusiness();

  return (
    <div className="bg-gray-50 p-5 md:p-10 flex items-center justify-center">
      <div className="flex flex-col gap-8 max-w-4xl w-full">
        {/* Header Section */}
        <div className="flex gap-2 flex-col">
          <div className="flex items-center gap-2">
            <div
              onClick={() => navigate(-1)}
              className="p-2 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 cursor-pointer hover:bg-gray-100"
            >
              <CaretLeft weight="bold" />
              <span>Back</span>
            </div>
          </div>
          <div className="text-2xl font-bold">Employment Details</div>
          <div>
            <span className="text-sm text-gray-500">
              Please fill in your employment details to comply with KYC regulations.
            </span>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-lg w-full shadow-sm flex-col gap-5 flex p-5">
          <Formik
            initialValues={{
              employmentID: "",
              employment_status: user?.compliance?.employment_status || "",
              company_name: user?.compliance?.company_name || "",
              business_start_date: user?.compliance?.business_start_date || "",
              company_sector: user?.compliance?.company_sector || "", // Fixed typo in your code (company_state vs sector)
              companyHouseNumber: "",
              companyStreetName: "",
              companyPostcode: "",
              company_state: "",
              companyCountry: "",
              officialPhoneNumber: "",
              officialEmail: "",
              monthly_income: "",
              managerName: "",
              managerEmail: "",
              managerAddress: "",
              business_type: "", // Add business_type
              registrationNumber: "", 
              registrationName: "",
              personalPhoneNumber: user?.compliance?.personal_phone_number || "",
            }}
            onSubmit={(values) => {
              const formattedValues = {
                ...values,
                officialPhoneNumber: values.officialPhoneNumber?.replace(/^\+/, ""),
                personalPhoneNumber: values.personalPhoneNumber?.replace(/^\+/, ""),
              };
              updateEmploymentInfo(formattedValues);
            }}
          >
            {({ values, setFieldValue, isValid, dirty }) => (
              <Form className="bg-white w-full rounded-xl shadow-sm flex items-center flex-col gap-5">
                
                {/* Employment Status Dropdown */}
                <div className="flex flex-col gap-2 w-full">
                  <label htmlFor="employment_status" className="text-sm font-bold text-gray-700">
                    Employment Status
                  </label>
                  <Field
                    as="select"
                    id="employment_status"
                    name="employment_status"
                    className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                  >
                    <option value="">Select</option>
                    <option value="employed">Employed</option>
                    <option value="selfEmployed">Self-employed</option>
                    <option value="unemployed">Unemployed</option>
                  </Field>
                  <ErrorMessage name="employment_status" component="div" className="text-red-500" />
                </div>

                {/* Conditional Fields based on Status */}
                {values.employment_status !== "unemployed" && (
                  <>
                    {/* Verification Section for Self-Employed */}
                    {values.employment_status === "selfEmployed" && (
                      <>
                        {/* Business Name Field for Verification refinement */}
                        <div className="flex flex-col gap-2 w-full">
                          <label className="text-sm font-bold text-gray-700">
                            Registered Business Name (Optional)
                          </label>
                          <Field
                            type="text"
                            name="registrationName"
                            placeholder="Enter name to refine search (e.g. Dangote)"
                            className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                          />
                          <div className="text-xs text-gray-500">
                            Providing this helps verify older companies or resolve conflicts.
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 w-full">
                          <label htmlFor="business_type" className="text-sm font-bold text-gray-700">
                            Business Type
                          </label>
                          <Field
                            as="select"
                            id="business_type"
                            name="business_type"
                            className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                setFieldValue("business_type", e.target.value);
                                setIsBusinessVerified(false); // Reset verification on change
                            }}
                          >
                            <option value="">Select Business Type</option>
                            <option value="bn">Business Name (BN)</option>
                            <option value="rc">Private Limited Company (RC)</option>
                            <option value="it">Incorporated Trustees (IT)</option>
                            <option value="ll">Limited Liability Partnership (LL)</option>
                            <option value="lp">Limited Partnership (LP)</option>
                          </Field>
                           <ErrorMessage name="business_type" component="div" className="text-red-500" />
                        </div>

                        <div className="flex flex-col gap-2 w-full">
                          <label className="text-sm font-bold text-gray-700">
                            CAC Registration Number
                          </label>
                          <div className="flex gap-2">
                            <Field
                              type="text"
                              name="registrationNumber"
                              placeholder="e.g. 123456"
                              className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full uppercase"
                              // Force uppercase for better UX
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setFieldValue("registrationNumber", e.target.value.toUpperCase().replace(/\s/g, ''));
                                  setIsBusinessVerified(false); // Reset verification on change
                              }}
                            />
                            <button
                              type="button"
                              disabled={isVerifyingBusiness || !values.registrationNumber || !values.business_type}
                              onClick={() => {
                                verifyBusiness(
                                  {
                                    rcNumber: values.registrationNumber,
                                    type: values.business_type,
                                  } as any,
                                  {
                                    onSuccess: (response) => {
                                      const companyData = response?.data?.entity;
                                      
                                      if (companyData) {
                                        toast.success("Business Verified Successfully!");
                                        setIsBusinessVerified(true);
                                        
                                        // 1. Auto-fill Name
                                        if (companyData.company_name) {
                                           setFieldValue("company_name", companyData.company_name);
                                        }

                                        // 2. Auto-fill Address
                                        if (companyData.address) {
                                          setFieldValue("companyStreetName", companyData.address);
                                        }

                                        // 3. Auto-fill Date
                                        if (companyData.date_of_registration) {
                                          const date = new Date(companyData.date_of_registration);
                                          const formattedDate = date.toISOString().split('T')[0];
                                          setFieldValue("business_start_date", formattedDate);
                                        }
                                        
                                        // 4. Auto-fill Email
                                        if (companyData.email) {
                                            setFieldValue("officialEmail", companyData.email);
                                        }
                                      } else {
                                          toast.info("Verification complete, but no data returned to auto-fill.");
                                      }
                                    },
                                    onError: (err: any) => {
                                      console.error("Verification Failed:", err);
                                      toast.error(
                                        err?.response?.data?.message || "Business verification failed. Please check the number."
                                      );
                                      setIsBusinessVerified(false);
                                    },
                                  }
                                );
                              }}
                              className="bg-deepBlue text-white px-6 py-3 rounded-lg font-bold text-sm disabled:opacity-50 flex items-center justify-center min-w-[100px]"
                            >
                              {isVerifyingBusiness ? "..." : "Verify"}
                            </button>
                          </div>
                          <ErrorMessage name="registrationNumber" component="div" className="text-red-500" />
                        </div>
                      </>
                    )}

                    {/* Company Name */}
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="company_name" className="text-sm font-bold text-gray-700">
                        {values.employment_status === "selfEmployed" ? "Business " : "Organization "} Name
                      </label>
                      <Field
                        type="text"
                        id="company_name"
                        name="company_name"
                        placeholder="Organisation Name"
                        className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full bg-gray-50"
                        // Optional: make it read-only if verified to prevent editing
                        // readOnly={values.employment_status === "selfEmployed" && !!values.company_name}
                      />
                      <ErrorMessage name="company_name" component="div" className="text-red-500" />
                    </div>

                    {/* Rest of the form fields... (Employment ID, Sector, Date, Address, Phone, Email) */}
                    
                    {/* Example: Business Start Date */}
                    <div className="flex flex-col gap-2 w-full">
                        <label htmlFor="business_start_date" className="text-sm font-bold text-gray-700">
                            {values.employment_status === "selfEmployed" ? "Business" : "Job"} Start Date
                        </label>
                        <Field
                            type="date"
                            id="business_start_date"
                            name="business_start_date"
                            className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage name="business_start_date" component="div" className="text-red-500" />
                    </div>

                    {/* Company HouseNumber and street name */}
                    <div className="w-full grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-2">
                      <div className="flex flex-col gap-2 w-full flex-1">
                        <label
                          htmlFor="companyHouseNumber"
                          className="text-sm font-bold text-gray-700"
                        >
                          {values.employment_status === "selfEmployed"
                            ? "Business "
                            : "Organization "}
                          House Number
                        </label>
                        <Field
                          type="text"
                          id="companyHouseNumber"
                          name="companyHouseNumber"
                          placeholder="Organisation house number"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="companyHouseNumber"
                          component="div"
                          className="text-red-500"
                        />
                      </div>
                      <div className="flex flex-col gap-2 w-full flex-1">
                        <label
                          htmlFor="companyStreetName"
                          className="text-sm font-bold text-gray-700"
                        >
                          {values.employment_status === "selfEmployed"
                            ? "Business "
                            : "Organization "}
                          Street Name
                        </label>
                        <Field
                          type="text"
                          id="companyStreetName"
                          name="companyStreetName"
                          placeholder="Organisation street name"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="companyStreetName"
                          component="div"
                          className="text-red-500"
                        />
                      </div>
                    </div>
                    {/* Company Region and Postal code */}
                    <div className="w-full grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-2">
                      <div className="flex flex-col gap-2 w-full flex-1">
                        <label
                          htmlFor="companyRegion"
                          className="text-sm font-bold text-gray-700"
                        >
                          {values.employment_status === "selfEmployed"
                            ? "Business "
                            : "Organization "}
                          Region
                        </label>
                        <Field
                          type="text"
                          id="companyRegion"
                          name="companyRegion"
                          placeholder="Organisation region"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="companyRegion"
                          component="div"
                          className="text-red-500"
                        />
                      </div>
                      <div className="flex flex-col gap-2 w-full flex-1">
                        <label
                          htmlFor="companyPostalcode"
                          className="text-sm font-bold text-gray-700"
                        >
                          {values.employment_status === "selfEmployed"
                            ? "Business "
                            : "Organization "}
                          Postal Code
                        </label>
                        <Field
                          type="text"
                          id="companyPostalcode"
                          name="companyPostalcode"
                          placeholder="Organisation postal code"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="companyPostalcode"
                          component="div"
                          className="text-red-500"
                        />
                      </div>
                    </div>
                    {/* Company Country */}
                    <div className="flex flex-col gap-2 w-full flex-1">
                      <label
                        htmlFor="companyCountry"
                        className="text-sm font-bold text-gray-700"
                      >
                        {values.employment_status === "employed"
                          ? "Business "
                          : "Organization "}
                        Country
                      </label>
                      <Field
                        as="select"
                        type="text"
                        id="companyCountry"
                        name="companyCountry"
                        placeholder="Organisation country"
                        className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                      >
                        <option disabled value="Afghanistan">
                          Select Country
                        </option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Nigeria">Nigeria</option>
                        {/* Add other countries here or map from a list */}
                      </Field>
                      <ErrorMessage
                        name="companyCountry"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="w-full grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-2">
                      {/* Official Phone Number */}
                      <div className="flex flex-col gap-2 w-full">
                        <label
                          htmlFor="officialPhoneNumber"
                          className="text-sm font-bold text-gray-700"
                        >
                          Official Phone Number
                        </label>

                        <BasePhoneInput
                          international
                          countryCallingCodeEditable={false}
                          id="officialPhoneNumber"
                          name="officialPhoneNumber"
                          className="rounded-lg border border-gray-300 shadow px-4 w-full"
                          withCountryCallingCode
                          defaultCountry="NG"
                          onChange={(phone: string | undefined) => {
                            setFieldValue(
                              "officialPhoneNumber",
                              phone?.toString()
                            );
                          }}
                        />

                        <ErrorMessage
                          name="officialPhoneNumber"
                          component="div"
                          className="text-red-500"
                        />
                      </div>

                      {/* Official Email */}
                      <div className="flex flex-col gap-2 w-full">
                        <label
                          htmlFor="officialEmail"
                          className="text-sm font-bold text-gray-700"
                        >
                          Official Email
                        </label>
                        <Field
                          type="email"
                          id="officialEmail"
                          name="officialEmail"
                          placeholder="example@company.com"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="officialEmail"
                          component="div"
                          className="text-red-500"
                        />
                      </div>
                    </div>

                    {/* Monthly Income */}
                    <div className="flex flex-col gap-2 w-full">
                      <label
                        htmlFor="monthly_income"
                        className="text-sm font-bold text-gray-700"
                      >
                        Monthly Income
                      </label>
                      <CurrencyField
                        name="monthly_income"
                        placeholder="₦100,000"
                      />
                      <ErrorMessage
                        name="monthly_income"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    <div className="flex flex-col gap-2 w-full">
                      <label
                        htmlFor="personalPhoneNumber"
                        className="text-sm font-bold text-gray-700"
                      >
                        Personal Phone Number
                      </label>
                      <BasePhoneInput
                        international
                        countryCallingCodeEditable={false}
                        id="personalPhoneNumber"
                        name="personalPhoneNumber"
                        className="rounded-lg border border-gray-300 shadow px-4 w-full"
                        withCountryCallingCode
                        defaultCountry="NG"
                        onChange={(phone: string | undefined) => {
                          setFieldValue(
                            "personalPhoneNumber",
                            phone?.toString()
                          );
                        }}
                      />
                      <ErrorMessage
                        name="personalPhoneNumber"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    {/* CAC Upload for Self-employed */}
                    {values.employment_status === "selfEmployed" && (
                      <div className="flex flex-col gap-2 w-full">
                        <label
                          htmlFor="cacDocument"
                          className="text-sm font-bold text-gray-700"
                        >
                          Upload CAC Document
                        </label>
                        <input
                          id="cacDocument"
                          name="cacDocument"
                          type="file"
                          //   onChange={(event) => {
                          //     setFieldValue(
                          //       "cacDocument",
                          //       event.currentTarget.files[0]
                          //     );
                          //   }}
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                      </div>
                    )}

                    {/* Manager's Name, Email and Address */}
                    {values.employment_status === "employed" && (
                      <>
                        {/* Company Region and Postal code */}
                        <div className="w-full grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-2">
                          <div className="flex flex-col gap-2 w-full flex-1">
                            <label
                              htmlFor="managerName"
                              className="text-sm font-bold text-gray-700"
                            >
                              Manager's Name
                            </label>
                            <Field
                              type="text"
                              id="managerName"
                              name="managerName"
                              placeholder="Manager's name"
                              className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                            />
                            <ErrorMessage
                              name="managerName"
                              component="div"
                              className="text-red-500"
                            />
                          </div>
                          <div className="flex flex-col gap-2 w-full flex-1">
                            <label
                              htmlFor="managerEmail"
                              className="text-sm font-bold text-gray-700"
                            >
                              Manager's Email
                            </label>
                            <Field
                              type="text"
                              id="managerEmail"
                              name="managerEmail"
                              placeholder="Manager's email"
                              className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                            />
                            <ErrorMessage
                              name="managerEmail"
                              component="div"
                              className="text-red-500"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 w-full flex-1">
                          <label
                            htmlFor="managerAddress"
                            className="text-sm font-bold text-gray-700"
                          >
                            Manager's Address
                          </label>
                          <Field
                            type="text"
                            id="managerAddress"
                            name="managerAddress"
                            placeholder="Manager's address"
                            className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                          />
                          <ErrorMessage
                            name="managerAddress"
                            component="div"
                            className="text-red-500"
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Submit Button */}
                <div className="flex items-center gap-5 w-full mt-4">
                  <button
                    disabled={isUpdatingInfo || !isValid || !dirty || (values.employment_status === "selfEmployed" && !isBusinessVerified)}
                    type="submit"
                    className="p-3 px-5 flex-1 border-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm hover:bg-deepBlue/90 transition-colors disabled:opacity-70"
                  >
                    {isUpdatingInfo ? "Saving..." : "Save"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default EmploymentDetail;